import React from "react";
import styled from "styled-components";
import theme from "../../../../assets/theme";
import useLoader from "../../../../hooks/useLoader";
import { Paper, Typography, Modal } from "@material-ui/core";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../../common/Buttons";
import { Formik } from "formik";
import { CustomFileUpload } from "../../../common/FormInputs";
import { useSnackbar } from "notistack";
import CaseService from "../../../../services/CaseService";
import { Label } from "../../ManagementTools/component/SplitAndUpload";
import CustomSelect from "../../NoticeGeneration/component/CustomSelect";
import { caseUploadValidationSchema } from "../../../../helpers/validationSchema";
import AWSService from "../../../../services/AWSService";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import * as XLSX from "xlsx";

const UploadCaseData = ({ uploadModal, setUploadModal }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function s3Upload(file, field, setFieldValue, setFieldError) {
    if (file) {
      try {
        setLoader({
          state: true,
          message: `Uploading file ${file.name} ...`,
        });
        const isValidExcel = await validateExcelHeader(file);
        if (!isValidExcel) {
          return;
        }
        const payload = {
          key: file.name,
        };
        const s3Response = await AWSService.getS3URL(payload);
        if (s3Response?.url) {
          const res = await AWSService.uploadToS3(s3Response.url, file);
          setFieldValue(field, res);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        setFieldError(field, message);
      } finally {
        setLoader({ state: false });
      }
    }
  }

  /**
   * @description Read xlsx file
   * @param {*} file
   * @returns
   */

  const validateExcelHeader = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, {
      type: "array",
    });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
    });
    const headerRow = data[0];
    if (!Array.isArray(headerRow) || headerRow.length === 0) {
      enqueueSnackbar("Excel file is missing header row", {
        variant: "error",
      });
      return false;
    }
    const hasCaseIdColumn = headerRow.some((header) => {
      const normalizedHeader = String(header || "")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase();

      return ["case id", "caseid"].includes(normalizedHeader);
    });
    if (!hasCaseIdColumn) {
      enqueueSnackbar("Case ID column not found", {
        variant: "error",
      });
      return false;
    }
    return true;
  };

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: `upload case data...` });
      const payload = {
        claimantPartyId: values.claimantPartyId,
        excelFile: values.excelFile,
      };
      const response = await CaseService.uploadCaseData(payload);
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
      resetForm();
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setUploadModal(false);
    }
  }

  return (
    <Modal
      open={uploadModal}
      onClose={() => setUploadModal(false)}
      disableRestoreFocus={true}
    >
      <DrawerContainer>
        <Container>
          <Paper elevation={3} style={{ padding: "16px" }}>
            <HeadingContainer>
              <Heading>Upload Case Data</Heading>
            </HeadingContainer>
            <Formik
              initialValues={{
                excelFile: "",
                claimantPartyId: "",
              }}
              enableReinitialize
              validateOnBlur
              validateOnChange
              validationSchema={caseUploadValidationSchema}
              onSubmit={onFormSubmit}
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                setFieldValue,
                setFieldError,
                handleBlur,
                isValid,
              }) => (
                <>
                  <FormContainer>
                    <CustomSelect
                      name="claimantPartyId"
                      trigger={true}
                      getNoticeTemplates={() => {}}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      menuItems={[]}
                    />
                  </FormContainer>

                  <FormContainer>
                    <Label
                      style={{
                        marginTop: 10,
                      }}
                    >
                      Upload XLSX File (Column name must be "Case ID or Contract Number"){" "}
                      <span className="required-star"> *</span>
                    </Label>
                    <div style={{ marginBottom: 13 }}>
                      <CustomFileUpload
                        setFile={(file) =>
                          s3Upload(
                            file,
                            `excelFile`,
                            setFieldValue,
                            setFieldError,
                          )
                        }
                        style={{
                          marginTop: 10,
                          width: "100%",
                        }}
                        error={touched["excelFile"] && errors["excelFile"]}
                        accept=".xlsx"
                        file={{ name: getFileName(values?.excelFile) }}
                        fileUrl={values?.excelFile}
                      />
                    </div>
                    <Flex>
                      <HalfWidth>
                        <PrimaryOutlinedCloseCTAButton
                          style={{ padding: "10px 10px" }}
                          onClick={() => setUploadModal(false)}
                        >
                          {"Cancel"}
                        </PrimaryOutlinedCloseCTAButton>
                      </HalfWidth>

                      <HalfWidth>
                        <PrimaryCTAButton
                          style={{ padding: "10px 10px" }}
                          onClick={handleSubmit}
                        >
                          {"Submit"}
                        </PrimaryCTAButton>
                      </HalfWidth>
                    </Flex>
                  </FormContainer>
                </>
              )}
            </Formik>
          </Paper>
        </Container>
      </DrawerContainer>
    </Modal>
  );
};

export default UploadCaseData;

const Container = styled.div`
  margin: 0 15px;
  background-color: white;
  outline: none;
  border-radius: 8px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const FormContainer = styled.div`
  padding-top: 5px;
  padding-left: 28px;
  padding-right: 54px;
  padding-bottom: 6px;
  & form {
    padding-top: 10px;
  }
`;

const Heading = styled(Typography)(() => ({
  color: "#293461",
  padding: "8px",
  fontFamily: theme.fonts.primaryFontBold,
  borderRadius: "5px",
  textAlign: "center",
}));

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 656px;
  }
`;

const HeadingContainer = styled.div`
  padding-right: 15px;
  padding-left: 15px;
  padding-top: 10px;
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 47px;
  padding-left: 24px;
  padding-right: 24px;
`;

const HalfWidth = styled.div`
  width: 48%;
`;
